/**
 * The ONE place that decides what a Playground chat's display toggles default to.
 *
 * Before this module the `showToolCalls` default was written as a bare `false`
 * literal at six separate sites — three `?? false` fallbacks in components and
 * three `storage.get(..., false)` calls in the store. Six literals is five
 * chances to flip four of them and ship a half-change that nothing catches,
 * because each site is independently plausible on its own.
 *
 * `showToolCalls` gates the tool-call rows in the chat feed. Those rows are the
 * only surface that distinguishes "the agent used a tool and it returned this"
 * from "the agent answered from its own knowledge" — every MCP failure mode
 * (a blanked input schema, a tool never invoked, an expired credential) is
 * indistinguishable from a normal answer without them. Defaulting that OFF made
 * the absence of evidence the default reading, so it defaults ON.
 *
 * `showThinking` already defaulted to `true` and is unchanged; it lives here so
 * the pair cannot drift apart again.
 */
import type { ChatSettings } from "@nexus/types"

import { STORAGE_KEYS } from "@/common/services/localStorage.keys"
import { storage } from "@/common/services/localStorage.service"

/** The display toggles this module owns, all required — no `undefined` reaches a consumer. */
export type PlaygroundDisplaySettings = Required<
  Pick<ChatSettings, "showToolCalls" | "showThinking">
>

export const PLAYGROUND_DISPLAY_DEFAULTS: PlaygroundDisplaySettings = {
  showToolCalls: true,
  showThinking: true
}

/**
 * The persisted display toggles for this browser, falling back to
 * {@link PLAYGROUND_DISPLAY_DEFAULTS} for any key that has never been written.
 *
 * Call this wherever a chat instance is seeded. Never inline a `storage.get`
 * with its own literal default — that is the drift this module exists to stop.
 */
export function readPlaygroundDisplaySettings(): PlaygroundDisplaySettings {
  return {
    showToolCalls: storage.get<boolean>(
      STORAGE_KEYS.PLAYGROUND_SHOW_TOOL_CALLS,
      PLAYGROUND_DISPLAY_DEFAULTS.showToolCalls
    ),
    showThinking: storage.get<boolean>(
      STORAGE_KEYS.PLAYGROUND_SHOW_THINKING,
      PLAYGROUND_DISPLAY_DEFAULTS.showThinking
    )
  }
}
